import React, { useState, useEffect } from 'react';
import TopBar from '../TopBar';
import ContextPanel from './ContextPanel';
import ImageViewer from './ImageViewer';
import WorkflowPanel from './WorkflowPanel';
import RecentHistory from './RecentHistory';
import { workflowService } from '../../services/workflowService';
import { WorkflowCatalogItem, WorkflowDetails } from '../../types/workflow';
import { useStudioContext } from '../../contexts/StudioContext';

interface WorkflowStudioProps {
  accessToken: string;
  userEmail: string;
  tenantName: string;
  tenantId: string;
}

interface JobProgress {
  jobId: string;
  // Canonical server vocabulary plus legacy values from v1 agents
  status: 'queued' | 'claimed' | 'processing' | 'completed' | 'failed'
    | 'pending' | 'running';
  progress: number;
  stage: string;
  message: string;
  // Structured sub-progress (sampler steps) - replaces parsing "value/max"
  // out of the message text
  currentStep?: number;
  totalSteps?: number;
  // Approximate number of messages in the processing queue while this job
  // is still queued (0 once it starts)
  queueDepth?: number;
  // Epoch ms — first time this job was observed by the UI. Drives the
  // elapsed-time clock in ProgressIndicator. Stays stable across polls.
  startedAt: number;
}

interface CompletedJob {
  jobId: string;
  workflowId: string;
  workflowName: string;
  createdAt: string;
  artifacts: Array<{
    filename: string;
    type: string;
  }>;
  thumbnailUrl?: string;
}

const WorkflowStudio: React.FC<WorkflowStudioProps> = ({
  accessToken,
  userEmail,
  tenantName,
  tenantId
}) => {
  // Workflow state
  const [workflows, setWorkflows] = useState<WorkflowCatalogItem[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowDetails | null>(null);
  const [loadingWorkflows, setLoadingWorkflows] = useState(true);

  // Job state
  const [currentJob, setCurrentJob] = useState<JobProgress | null>(null);
  const [recentJobs, setRecentJobs] = useState<CompletedJob[]>([]);
  const [selectedImage, setSelectedImage] = useState<CompletedJob | null>(null);

  // UI state
  const [error, setError] = useState<string | null>(null);

  // Studio context (selected products etc.)
  const { context } = useStudioContext();

  // Initialize workflow service
  useEffect(() => {
    console.log('🔄 [WorkflowStudio] useEffect triggered', { hasAccessToken: !!accessToken });
    if (!accessToken) {
      console.warn('⚠️ [WorkflowStudio] No access token available yet');
      return;
    }

    console.log('✅ [WorkflowStudio] Setting access token and loading data');
    workflowService.setAccessToken(accessToken);
    loadWorkflows();
    loadRecentJobs();
  }, [accessToken]);

  // Load available workflows
  const loadWorkflows = async () => {
    console.log('🔄 [WorkflowStudio] loadWorkflows() started');
    try {
      setLoadingWorkflows(true);
      console.log('🔄 [WorkflowStudio] Calling workflowService.listWorkflows()...');
      const response = await workflowService.listWorkflows(tenantId);
      console.log('✅ [WorkflowStudio] Received response:', response);
      const workflows = response.workflows || [];
      console.log(`✅ [WorkflowStudio] Setting ${workflows.length} workflows`);
      setWorkflows(workflows);

      // Auto-select first workflow if none selected
      if (workflows.length > 0 && !selectedWorkflow) {
        // Use rowKey as workflowId and version from metadata (new API structure)
        const firstWorkflow = workflows[0] as any;
        const workflowId = firstWorkflow.rowKey || firstWorkflow.workflowId;
        const version = firstWorkflow.version || '1.0.0';
        console.log(`🎯 [WorkflowStudio] Auto-selecting first workflow: ${workflowId}`);
        await selectWorkflow(workflowId, version);
      } else if (workflows.length === 0) {
        console.warn('⚠️ [WorkflowStudio] No workflows available');
      }
    } catch (err: any) {
      console.error('❌ [WorkflowStudio] Error loading workflows:', err);
      setError(`Failed to load workflows: ${err.message}`);
    } finally {
      setLoadingWorkflows(false);
      console.log('🔄 [WorkflowStudio] loadWorkflows() completed');
    }
  };

  // Load recent job history
  const loadRecentJobs = async () => {
    try {
      console.log('🔄 [WorkflowStudio] loadRecentJobs() started');
      const jobs = await workflowService.getJobHistory(10, tenantId);
      console.log('📦 [WorkflowStudio] Received jobs from API:', jobs.length, jobs);

      // Transform jobs to CompletedJob format
      const completedJobs: CompletedJob[] = jobs
        .filter((job: any) => {
          // Only include completed jobs
          if (job.status !== 'completed') {
            console.log(`⏭️ [WorkflowStudio] Skipping job ${job.jobId} - status: ${job.status}`);
            return false;
          }

          // Only include jobs that have at least one valid artifact (no error)
          const validArtifacts = job.artifacts?.filter((a: any) => !a.error) || [];
          if (validArtifacts.length === 0) {
            console.log(`⏭️ [WorkflowStudio] Skipping job ${job.jobId} - no valid artifacts (total: ${job.artifacts?.length || 0})`);
          }
          return validArtifacts.length > 0;
        })
        .map((job: any) => {
          // Filter out artifacts with errors
          const validArtifacts = job.artifacts?.filter((a: any) => !a.error) || [];

          console.log(`✅ [WorkflowStudio] Including job ${job.jobId} with ${validArtifacts.length} valid artifacts`);

          return {
            jobId: job.jobId,
            workflowId: job.workflowVersion, // Using workflowVersion as workflowId for now
            workflowName: job.workflowVersion,
            createdAt: job.createdAt,
            artifacts: validArtifacts,
            thumbnailUrl: validArtifacts[0]?.filename
              ? `/api/jobs/${job.jobId}/artifacts/${validArtifacts[0].filename}`
              : undefined
          };
        });

      console.log(`📋 [WorkflowStudio] Setting ${completedJobs.length} completed jobs in state`);
      setRecentJobs(completedJobs);

      // Auto-select most recent if none selected
      if (completedJobs.length > 0 && !selectedImage) {
        console.log(`🎯 [WorkflowStudio] Auto-selecting most recent job: ${completedJobs[0].jobId}`);
        setSelectedImage(completedJobs[0]);
      }
    } catch (err: any) {
      console.error('❌ [WorkflowStudio] Failed to load recent jobs:', err);
    }
  };

  // Select a workflow
  const selectWorkflow = async (workflowId: string, version: string) => {
    try {
      const details = await workflowService.getWorkflowDetails(workflowId, version, tenantId);
      setSelectedWorkflow(details);
      setError(null);
    } catch (err: any) {
      setError(`Failed to load workflow: ${err.message}`);
    }
  };

  // Submit a new job
  const handleJobSubmit = async (inputs: Record<string, any>) => {
    if (!selectedWorkflow) return;

    try {
      setError(null);

      // ── Required-context validation ─────────────────────────────────────
      // Every workflow-declared context section is treated as required: if
      // the author wired up a CCStudio_SheetSelector or CCStudio_ProductList
      // (or a legacy Context_Product input), an empty selection should block
      // submission with a clear, actionable message rather than failing later
      // with a missing-key error from the engine.
      const manifestInputs = selectedWorkflow.manifest.inputs || [];
      const missing: string[] = [];

      // Sheet-row sections
      for (const inp of manifestInputs) {
        if (inp.source !== 'context' || inp.type !== 'sheet-row') continue;
        const id = inp.context_id || inp.key;
        if (!context.sheetSelections[id]) {
          missing.push(inp.title || id);
        }
      }

      // Products section — native product-list OR legacy context_product bridge
      const nativeProductList = manifestInputs.find(
        i => i.source === 'context' && i.type === 'product-list'
      );
      const legacyContextProduct = !nativeProductList
        ? manifestInputs.find(i => i.type === 'context_product')
        : undefined;
      if ((nativeProductList || legacyContextProduct) && context.selectedProducts.length === 0) {
        missing.push(nativeProductList?.title || 'Products');
      }

      if (missing.length > 0) {
        setError(`Missing required context: ${missing.join(', ')}. Please make a selection in the left panel before running this workflow.`);
        return;
      }

      // Merge selected context into inputs using stable reserved keys.
      // Two channels coexist:
      //   1. Product context — flat reserved keys (product_name, product_sku, …)
      //      consumed by CCStudio_Input nodes wired to a CCStudio_ProductList.
      //   2. Sheet context — `${contextId}__tab` / `${contextId}__row` pairs
      //      consumed by CCStudio_SheetSelector nodes via compiler-emitted
      //      bindings.
      // Only non-empty values are injected so manually-typed form values
      // are never overwritten with an empty/undefined context field.
      const contextInputs: Record<string, any> = { ...inputs };
      const assign = (key: string, val: any) => { if (val !== undefined && val !== null && val !== '') contextInputs[key] = val; };

      if (context.selectedProducts.length > 0) {
        const primary = context.selectedProducts[0];

        assign('product_id',             primary.productId);
        assign('product_name',           primary.name);
        assign('product_description',    primary.description);
        assign('product_primary_image',  primary.primaryImage);
        assign('product_sku',            primary.sku);
        assign('product_variant',        primary.variant);
        assign('product_classification', primary.classification);
        assign('product_source_url',     primary.sourceUrl);  // Umage product page URL — used by CCStudio_UmageScraper

        // For multi-product workflows, also expose the full list
        if (context.selectedProducts.length > 1) {
          contextInputs.products = context.selectedProducts.map(p => ({
            id:           p.productId,
            name:         p.name,
            description:  p.description,
            primaryImage: p.primaryImage,
            sku:          p.sku,
            variant:      p.variant,
          }));
        }

        console.log(`🎯 [WorkflowStudio] Injecting context: ${context.selectedProducts.length} product(s)`, {
          primary: primary.name,
          keys: Object.keys(contextInputs).filter(k => k.startsWith('product')),
        });
      }

      // Inject sheet-driven context selections.
      //
      // For every CCStudio_SheetSelector the workflow declares with a context_id,
      // the compiler emits two bindings:
      //   ${contextId}__tab → node.inputs.tab
      //   ${contextId}__row → node.inputs.row_title
      // We mirror those keys here so the engine can resolve them at submit time.
      const sheetEntries = Object.values(context.sheetSelections);
      for (const sel of sheetEntries) {
        assign(`${sel.contextId}__tab`, sel.tab);
        assign(`${sel.contextId}__row`, sel.rowTitle);
      }
      if (sheetEntries.length > 0) {
        console.log(`📄 [WorkflowStudio] Injecting ${sheetEntries.length} sheet selection(s):`,
          sheetEntries.map(s => `${s.contextId}=${s.tab}/${s.rowTitle}`));
      }

      console.log('🚀 Submitting job:', {
        workflowId: selectedWorkflow.workflowId,
        version: selectedWorkflow.version,
        inputs: contextInputs,
        tenantId: tenantId
      });

      // Create job with active tenantId (for platform admin cross-tenant execution)
      const job = await workflowService.createJob({
        workflowId: selectedWorkflow.workflowId,
        version: selectedWorkflow.version,
        inputs: contextInputs
      }, tenantId);

      console.log('✅ Job created:', job);

      // Set current job for progress tracking
      setCurrentJob({
        jobId: job.id || job.jobId,
        status: 'queued',
        progress: 0,
        stage: 'queued',
        message: '',
        startedAt: Date.now()
      });

      // One per-tenant poll at a 2s cadence (the agent pushes granular
      // phase/step updates ~1/s, so 2s reads still look live) — replaces
      // the old 400ms-per-job polling that hammered the API.
      pollActiveJobs(job.id || job.jobId);
    } catch (err: any) {
      console.error('❌ Job creation failed:', err);
      setError(`Failed to create job: ${err.message}`);
    }
  };

  // Apply a job update from a poll. Returns true when the job has reached
  // a terminal state so the caller can stop reading.
  const applyJobUpdate = async (job: any, queueDepth: number = 0): Promise<boolean> => {
    setCurrentJob(prev => ({
      jobId: job.jobId,
      status: job.status,
      // `claimed` means an agent took the job but hasn't reported a phase
      // yet - surface that as its own stage so the label moves off "Queued".
      stage: job.status === 'claimed' && (!job.stage || job.stage === 'queued')
        ? 'claimed'
        : (job.stage || 'preparing'),
      progress: job.progress || 0,
      message: job.message || '',
      currentStep: job.currentStep,
      totalSteps: job.totalSteps,
      queueDepth: job.status === 'queued' ? queueDepth : 0,
      // Preserve the original startedAt so the elapsed clock keeps ticking
      // from the moment the user submitted, not from the latest update.
      startedAt: prev?.startedAt ?? Date.now()
    }));

    if (job.status === 'completed') {
      await loadRecentJobs();
      if (job.artifacts && job.artifacts.length > 0) {
        setSelectedImage({
          jobId: job.jobId,
          workflowId: job.workflowId || '',
          workflowName: job.workflowName || 'Unknown Workflow',
          createdAt: job.createdAt || new Date().toISOString(),
          artifacts: job.artifacts
        });
      }
      // Clear current job after a delay so the placeholder fades out
      // rather than swapping abruptly to the artifact.
      setTimeout(() => setCurrentJob(null), 3000);
      return true;
    }
    if (job.status === 'failed') {
      setError(`Job failed: ${job.error || 'Unknown error'}`);
      setCurrentJob(null);
      return true;
    }
    return false;
  };

  // Per-tenant live polling. One `GetJobs?active=true` request per 2s tick
  // returns ALL of the tenant's non-terminal jobs (single-partition query,
  // no quota lookup server-side) - previously this was one request per job
  // every 400ms against a cross-partition scan. The agent pushes phase/step
  // updates roughly once a second, so a 2s read cadence still renders as
  // live. Capped at ~10 minutes of wall-clock time. Transient errors are
  // swallowed and retried at the same cadence.
  const pollActiveJobs = async (jobId: string) => {
    const interval = 2000;
    let attempts = 0;
    const maxAttempts = 300;

    const poll = async () => {
      try {
        const { jobs, queueDepth } = await workflowService.getActiveJobs();
        const job = jobs.find((j: any) => j.jobId === jobId);

        if (!job) {
          // Not in the active set: the job reached a terminal state between
          // polls. Fetch its final record once for artifacts/error detail.
          const finalJob = await workflowService.getJobStatus(jobId);
          await applyJobUpdate(finalJob);
          return;
        }

        const terminal = await applyJobUpdate(job, queueDepth);
        if (terminal) return;

        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, interval);
        } else {
          setError('Job polling timeout - please refresh to check status');
          setCurrentJob(null);
        }
      } catch (err: any) {
        console.error('Error polling active jobs:', err);
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, interval);
        }
      }
    };
    poll();
  };

  // Handle image selection from history
  const handleImageSelect = (job: CompletedJob) => {
    setSelectedImage(job);
  };

  // A job is in flight for any non-terminal status (canonical v2 vocabulary
  // plus legacy 'pending'/'running' from v1 agents).
  const isJobRunning = !!currentJob &&
    currentJob.status !== 'completed' && currentJob.status !== 'failed';

  // Mobile panel state — only one panel open at a time on small screens
  const [mobilePanel, setMobilePanel] = useState<'none' | 'context' | 'workflow'>('none');

  // Detect small screen (iPhone-sized)
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  useEffect(() => {
    const checkScreen = () => setIsSmallScreen(window.innerWidth < 640);
    checkScreen();
    window.addEventListener('resize', checkScreen);
    return () => window.removeEventListener('resize', checkScreen);
  }, []);

  return (
    <div className="h-[100dvh] min-h-[100dvh] flex flex-col bg-gray-50 overflow-hidden">
      {/* Top Bar */}
      <TopBar
        tenantName={tenantName}
        userEmail={userEmail}
        currentJob={currentJob}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden min-h-0 relative">
        {/* Left Panel - Context (hidden on mobile, shown via bottom sheet) */}
        <div className="hidden sm:block sm:order-2 lg:order-none flex-shrink-0 sm:max-lg:h-[30dvh] lg:h-auto">
          <ContextPanel
            accessToken={accessToken}
            tenantId={tenantId}
            selectedWorkflow={selectedWorkflow}
          />
        </div>

        {/* Center - Image Viewer */}
        <div className="order-1 sm:order-1 lg:order-none flex-1 flex flex-col overflow-hidden min-h-0">
          <ImageViewer
            selectedImage={selectedImage}
            currentJob={currentJob}
            accessToken={accessToken}
          />

          {/* Bottom Panel - Recent History (hidden on small mobile) */}
          <div className="hidden sm:block">
            <RecentHistory
              jobs={recentJobs}
              selectedJobId={selectedImage?.jobId}
              onSelectJob={handleImageSelect}
              accessToken={accessToken}
            />
          </div>
        </div>

        {/* Right Panel - Workflow Controls (hidden on mobile, shown via bottom sheet) */}
        <div className="hidden sm:block sm:order-3 lg:order-none flex-shrink-0 sm:max-lg:h-[36dvh] lg:h-auto">
          <WorkflowPanel
            workflows={workflows}
            selectedWorkflow={selectedWorkflow}
            onSelectWorkflow={selectWorkflow}
            onSubmit={handleJobSubmit}
            loading={loadingWorkflows}
            disabled={isJobRunning}
          />
        </div>

        {/* ═══ Mobile Bottom Bar (iPhone only) ═══ */}
        {isSmallScreen && mobilePanel === 'none' && (
          <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 safe-b flex items-center gap-3 shadow-lg z-40">
            {/* Context button */}
            <button
              type="button"
              onClick={() => setMobilePanel('context')}
              className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Context
            </button>

            {/* Create button - primary action */}
            <button
              type="button"
              onClick={() => setMobilePanel('workflow')}
              disabled={isJobRunning}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-bold transition-colors ${
                isJobRunning
                  ? 'bg-gray-300 text-gray-500'
                  : 'bg-blue-500 hover:bg-blue-600 text-white shadow-md'
              }`}
            >
              {isJobRunning ? (
                <>
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Creating…
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create
                </>
              )}
            </button>

            {/* History button */}
            <button
              type="button"
              onClick={() => {
                if (recentJobs.length > 0 && !selectedImage) {
                  handleImageSelect(recentJobs[0]);
                }
              }}
              className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors"
              title="Recent creations"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
        )}

        {/* ═══ Mobile Bottom Sheet - Context ═══ */}
        {isSmallScreen && mobilePanel === 'context' && (
          <div className="absolute inset-0 z-50 flex flex-col bg-gray-900">
            {/* Sheet header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Context</h3>
              <button
                type="button"
                onClick={() => setMobilePanel('none')}
                className="p-2 text-gray-400 hover:text-white transition-colors"
                title="Close context panel"
                aria-label="Close context panel"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Sheet content */}
            <div className="flex-1 overflow-hidden">
              <ContextPanel
                accessToken={accessToken}
                tenantId={tenantId}
                selectedWorkflow={selectedWorkflow}
              />
            </div>
          </div>
        )}

        {/* ═══ Mobile Bottom Sheet - Workflow ═══ */}
        {isSmallScreen && mobilePanel === 'workflow' && (
          <div className="absolute inset-0 z-50 flex flex-col bg-white">
            {/* Sheet header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Create</h3>
              <button
                type="button"
                onClick={() => setMobilePanel('none')}
                className="p-2 text-gray-400 hover:text-gray-700 transition-colors"
                title="Close workflow panel"
                aria-label="Close workflow panel"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Sheet content */}
            <div className="flex-1 overflow-hidden">
              <WorkflowPanel
                workflows={workflows}
                selectedWorkflow={selectedWorkflow}
                onSelectWorkflow={selectWorkflow}
                onSubmit={(inputs) => {
                  handleJobSubmit(inputs);
                  setMobilePanel('none'); // Close sheet after submit
                }}
                loading={loadingWorkflows}
                disabled={isJobRunning}
              />
            </div>
          </div>
        )}
      </div>

      {/* Error Toast */}
      {error && (
        <div className="fixed bottom-4 right-4 left-4 sm:left-auto bg-red-500 text-white px-5 py-3 rounded-lg shadow-lg flex items-center space-x-3 max-w-md z-50 safe-b">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-auto text-white hover:text-gray-200 touch-target flex items-center justify-center"
            title="Dismiss error"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default WorkflowStudio;

